import { CompanySelect, canonCompany, useCompanies } from "../lib/companies";
import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Save, Trash2, FileText, Plus,
} from "lucide-react";
import { money, money2, QUOTE_STATUSES } from "../lib/finance";
import { API } from "../lib/apiClient";
import { CoverageTemplatePicker } from "./CoverageTemplatePicker";
import { Modal, Field } from "./common";

const low = (s) => (s || "").toLocaleLowerCase("tr");
export const branchNeeds = (b) => {
  const x = low(b);
  const vehicle = /trafik|kasko|imm|i̇mm|ımm/.test(x);
  return { id: vehicle || x.includes("tamamlay"), vehicle };
};
const emptyOffer = () => ({ company: "", quoteNo: "", premium: "", netPremium: "", coverages: "" });
const wantsCoverages = (b) => /kasko|tamamlay|tss|imm|i̇mm|ımm/.test(low(b));

function OfferRows({ offers, setOffers, canAdd, showCov, branch, reqCov = "" }) {
  const patch = (i, k, v) => setOffers((l) => l.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  return (
    <div className="offer-rows" data-testid="quote-offer-rows">
      {offers.map((o, i) => (
        <div key={i} className="offer-row" data-testid={`quote-offer-${i}`}>
          <CompanySelect label={offers.length > 1 ? `${i + 1}. Şirket` : "Şirket"} name={`quote-company-${i}`} testId={i === 0 ? "quote-company-select" : undefined} value={o.company} onChange={(e) => patch(i, "company", e.target.value)} />
          <Field label="Teklif No" name={`quote-no-${i}`} value={o.quoteNo} onChange={(e) => patch(i, "quoteNo", e.target.value)} />
          <Field label="Brüt Prim (₺)" name={i === 0 ? "quote-premium" : `quote-premium-${i}`} type="number" step="0.01" value={o.premium} onChange={(e) => patch(i, "premium", e.target.value)} />
          <Field label="Net Prim (₺)" name={i === 0 ? "quote-netpremium" : `quote-netpremium-${i}`} type="number" step="0.01" value={o.netPremium} onChange={(e) => patch(i, "netPremium", e.target.value)} />
          {showCov && <label className="field offer-cov"><span>Teminatlar</span><textarea rows="2" value={o.coverages || ""} onChange={(e) => patch(i, "coverages", e.target.value)} placeholder="Örn. İMM: 1.000.000 TL, İkame araç: 15 gün, Cam: Muafiyetsiz" data-testid={`quote-coverages-${i}`} />
            <CoverageTemplatePicker idx={i} company={o.company} branch={branch} text={o.coverages} onPick={(t) => patch(i, "coverages", o.coverages && !window.confirm("Mevcut teminat metninin yerine şablon yazılsın mı?") ? `${o.coverages}\n${t}` : t)} /></label>}
          {offers.length > 1 && <button type="button" className="icon-button offer-del" onClick={() => setOffers((l) => l.filter((_, j) => j !== i))} title="Kaldır" data-testid={`quote-offer-remove-${i}`}><Trash2 size={15} /></button>}
        </div>
      ))}
      {canAdd && <button type="button" className="outline-button offer-add" onClick={() => setOffers((l) => [...l, { ...emptyOffer(), coverages: reqCov }])} data-testid="quote-add-company"><Plus size={14} /> Şirket Teklifi ekle</button>}
    </div>
  );
}

export function QuoteModal({ initial, customers, branches, parsePdfFn, onSave, onClose, onUploadPdf, onRemovePdf, onOpenPdf }) {
  const seed = initial || {};
  const companies = useCompanies();
  const [form, setForm] = useState({
    id: seed.id || "",
    customerId: seed.customerId || "",
    customerName: seed.customerName || "",
    customerPhone: seed.customerPhone || "",
    customerEmail: seed.customerEmail || "",
    branch: seed.branch || (branches[0]?.name || ""),
    company: seed.company || "",
    plate: seed.plate || "",
    tcKimlik: seed.tcKimlik || "",
    birthDate: seed.birthDate || "",
    ruhsatSeriNo: seed.ruhsatSeriNo || "",
    premium: seed.premium || "",
    netPremium: seed.netPremium ?? "",
    commissionRate: seed.commissionRate ?? 15,
    status: seed.status || "Taslak",
    note: seed.note || "",
    source: seed.source || "manual",
    expiryDate: seed.expiryDate || "",
  });
  const reqCov = seed.requestCoverages || "";
  const [offers, setOffers] = useState(() => (seed.offers?.length ? seed.offers.map((o) => ({ ...o, premium: o.premium ?? "", netPremium: o.netPremium ?? "", coverages: o.coverages || "" }))
    : [{ company: seed.company || "", quoteNo: seed.quoteNo || "", premium: seed.premium || "", netPremium: seed.netPremium ?? "", coverages: reqCov }]));
  const [pendingPdf, setPendingPdf] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState("");
  const [existingPdfs, setExistingPdfs] = useState(initial?.pdfs || []);
  const pdfFileRef = useRef(null);
  useEffect(() => { setExistingPdfs(initial?.pdfs || []); }, [initial?.id, initial?.pdfs]);
  const needs = branchNeeds(form.branch);
  const bind = (name) => ({ value: form[name] ?? "", onChange: (e) => setForm((f) => ({ ...f, [name]: e.target.value })) });
  const pickCustomer = (name) => {
    const c = customers.find((x) => x.name === name);
    if (c) {
      setForm((f) => ({ ...f, customerId: c.id, customerName: c.name, customerPhone: c.phone || f.customerPhone, customerEmail: c.email || f.customerEmail }));
    } else {
      setForm((f) => ({ ...f, customerId: "", customerName: name }));
    }
  };

  const handlePdfPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { setPdfMsg("Lütfen PDF dosyası seçin."); return; }
    setPdfBusy(true); setPdfMsg("PDF okunuyor...");
    try {
      const parsed = await parsePdfFn(file, false);
      // parsed is policy-shaped; map to quote fields (only fill what's empty, don't overwrite user input)
      setForm((f) => {
        const next = { ...f };
        const take = (k, v) => { if (!next[k] && v) next[k] = v; };
        take("customerName", parsed.customer);
        take("branch", parsed.branch);
        take("plate", parsed.plate);
        if (parsed.commissionRate && !next.commissionRate) next.commissionRate = parsed.commissionRate;
        // Preserve policy no + dates in note for reference
        const extras = [];
        if (parsed.policeNo) extras.push(`Teklif No: ${parsed.policeNo}`);
        if (parsed.start) extras.push(`Başlangıç: ${parsed.start}`);
        if (parsed.end) extras.push(`Bitiş: ${parsed.end}`);
        if (extras.length && !next.note.includes("Teklif No")) {
          next.note = (next.note ? next.note + "\n" : "") + extras.join(" · ");
        }
        return next;
      });
      setOffers((l) => l.map((o, i) => (i === 0 ? {
        ...o,
        company: o.company || canonCompany(parsed.company, companies) || "",
        quoteNo: o.quoteNo || parsed.policeNo || "",
        premium: o.premium || parsed.premium || "",
        netPremium: o.netPremium !== "" ? o.netPremium : (parsed.netPremium ?? ""),
      } : o)));
      // If quote already exists (editing), upload PDF immediately
      if (form.id && onUploadPdf) {
        try {
          const updated = await onUploadPdf(form.id, file, "Teklif PDF");
          setExistingPdfs(updated?.pdfs || []);
          setPdfMsg("✓ PDF yüklendi ve alanlar dolduruldu.");
        } catch (e2) {
          setPendingPdf(file);
          setPdfMsg("PDF okunup alanlar dolduruldu. Yüklenemedi: " + (e2?.response?.data?.detail || e2?.message || ""));
        }
      } else {
        setPendingPdf(file);
        setPdfMsg(`✓ Alanlar dolduruldu. Kaydettiğinizde PDF (${file.name}) teklife eklenecek.`);
      }
    } catch (e) {
      // Even if client-side parsing fails, keep the file so it still gets uploaded on save
      setPendingPdf(file);
      setPdfMsg(`PDF içeriği okunamadı ama dosya teklife eklenecek. Alanları elle doldurabilirsiniz. (${e?.message || e})`);
    } finally { setPdfBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) { window.alert("Müşteri adı zorunludur."); return; }
    if (offers.length > 1 && offers.some((x) => !x.company)) { window.alert("Her şirket teklifi için şirket seçin."); return; }
    const { id: _oid, coverages: _c, ...first } = offers[0];
    await onSave({ ...(form.id ? seed : {}), ...form, ...first, offers: offers.length > 1 || offers[0].coverages ? offers : [], id: form.id || undefined }, pendingPdf);
  };
  return (
    <Modal title={initial?.id ? "Teklifi Düzenle" : "Yeni Teklif"} onClose={onClose}>
      <form onSubmit={submit} className="modal-body" data-testid="quote-form">
        {parsePdfFn && (
          <div className="ocr-dropzone" data-testid="quote-pdf-dropzone" style={{ border: "2px dashed #bfdbfe", background: "#eff6ff", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <strong style={{ display: "block", fontSize: 13, color: "#1447b8" }}>Teklif PDF'inden otomatik doldur</strong>
              <small className="muted">Şirket, müşteri, prim, plaka vb. PDF'ten okunur; PDF teklife iliştirilir.</small>
            </div>
            <input ref={pdfFileRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }} data-testid="quote-pdf-file" onChange={handlePdfPick} />
            <button type="button" className="outline-button" data-testid="quote-pdf-pick-button" disabled={pdfBusy} onClick={() => pdfFileRef.current?.click()}>
              {pdfBusy ? "Okunuyor..." : (<><FileText size={14} /> PDF yükle</>)}
            </button>
          </div>
        )}
        {pdfMsg && <div className="small" data-testid="quote-pdf-msg" style={{ marginBottom: 8, color: pdfMsg.startsWith("✓") ? "#059669" : "#b45309" }}>{pdfMsg}</div>}
        {!!existingPdfs.length && (
          <div className="panel" style={{ background: "#f8fafc", padding: 10, borderRadius: 8, marginBottom: 14 }}>
            <strong className="small" style={{ display: "block", marginBottom: 6 }}>Ekli PDF'ler</strong>
            {existingPdfs.map((att) => (
              <div key={att.id} className="small" style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }} data-testid={`quote-pdf-row-${att.id}`}>
                <FileText size={14} />
                <button type="button" className="ghost-button" data-testid={`quote-pdf-open-${att.id}`} onClick={() => onOpenPdf && onOpenPdf(form.id, att)} style={{ flex: 1, justifyContent: "flex-start", textAlign: "left" }}>{att.filename}</button>
                {att.label && <span className="muted">{att.label}</span>}
                <button type="button" className="ghost-button danger" data-testid={`quote-pdf-del-${att.id}`} onClick={async () => { if (onRemovePdf && form.id) { const updated = await onRemovePdf(form.id, att.id); if (updated) setExistingPdfs(updated.pdfs || []); } }} title="Sil"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="form-grid">
          <label className="field">
            <span>Müşteri Adı *</span>
            <input data-testid="quote-customer-input" list="quote-customers" value={form.customerName} onChange={(e) => pickCustomer(e.target.value)} required />
            <datalist id="quote-customers">
              {customers.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </label>
          <Field label="Telefon" name="quote-phone" value={form.customerPhone} onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))} />
          <Field label="E-posta" name="quote-email" value={form.customerEmail} onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))} />
          <label className="field">
            <span>Branş</span>
            <select data-testid="quote-branch-select" {...bind("branch")}>
              {branches.map((b) => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
            </select>
          </label>
          {(needs.id || form.tcKimlik) && <Field label="TC Kimlik No" name="quote-tc" value={form.tcKimlik} onChange={(e) => setForm((f) => ({ ...f, tcKimlik: e.target.value.replace(/\D/g, "").slice(0, 11) }))} />}
          {(needs.id || form.birthDate) && <Field label="Doğum Tarihi" name="quote-birth" type="date" value={form.birthDate} onChange={(e) => setForm((f) => ({ ...f, birthDate: e.target.value }))} />}
          {(needs.vehicle || form.plate) && <Field label="Plaka" name="quote-plate" value={form.plate} onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value.toUpperCase() }))} />}
          {(needs.vehicle || form.ruhsatSeriNo) && <Field label="Ruhsat Seri No" name="quote-ruhsat" value={form.ruhsatSeriNo} onChange={(e) => setForm((f) => ({ ...f, ruhsatSeriNo: e.target.value.toUpperCase().replace(/[^A-ZÇĞİÖŞÜ0-9]/g, "").slice(0, 8) }))} />}
          <Field label="Komisyon %" name="quote-rate" type="number" step="0.01" value={form.commissionRate} onChange={(e) => setForm((f) => ({ ...f, commissionRate: e.target.value }))} />
          <label className="field">
            <span>Durum</span>
            <select data-testid="quote-status-select" {...bind("status")}>
              {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Field label="Geçerlilik Tarihi" name="quote-expiry" type="date" value={form.expiryDate} onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))} />
        </div>
        {reqCov && <small className="req-cov-hint" data-testid="quote-request-coverages">Müşteri talebi: {reqCov.replace(/\n/g, " · ")}</small>}
        <OfferRows offers={offers} setOffers={setOffers} canAdd showCov={wantsCoverages(form.branch)} branch={form.branch} reqCov={reqCov} />
        <label className="field full">
          <span>Not</span>
          <textarea data-testid="quote-note-input" rows="3" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}></textarea>
        </label>
        <div className="modal-actions">
          <button type="button" className="outline-button" data-testid="quote-cancel-button" onClick={onClose}>Vazgeç</button>
          <button type="submit" className="primary-button" data-testid="quote-save-button"><Save size={15} /> Kaydet</button>
        </div>
      </form>
    </Modal>
  );
}

